<template>
  <div class="prod-stack">
    <ProductionSectionHead title="People" subtitle="Who’s in front of and behind the camera for this shoot." />
    <section v-for="role in roles" :key="role" class="move-card prod-panel !p-5">
      <div class="flex items-center justify-between gap-3 mb-4">
        <div class="flex items-center gap-3 min-w-0">
          <span class="prod-role-icon">
            <span class="material-icons">{{ roleIcons[role] }}</span>
          </span>
          <div class="min-w-0">
            <h3 class="text-base font-bold move-text">{{ memberRoleLabels[role] }}</h3>
            <p class="text-xs move-text-faint">{{ peopleFor(role).length ? `${peopleFor(role).length} assigned` : 'Unassigned' }}</p>
          </div>
        </div>
        <button type="button" class="move-btn-primary !py-2 !px-3 shrink-0" @click="openAdd(role)">
          <span class="material-icons text-lg">person_add</span>
          Add
        </button>
      </div>

      <div v-if="peopleFor(role).length" class="space-y-2">
        <div
          v-for="person in peopleFor(role)"
          :key="person.id"
          class="prod-person-row"
        >
          <span class="prod-person-avatar">{{ initials(person.name) }}</span>
          <div class="min-w-0 flex-1">
            <p class="font-semibold move-text truncate">{{ person.name }}</p>
            <p class="text-xs move-text-faint truncate">
              {{ person.email || person.phone || 'External' }}
              <span v-if="person.crew_title"> · {{ person.crew_title }}</span>
            </p>
          </div>
          <button
            type="button"
            class="move-icon-btn move-icon-btn--danger"
            aria-label="Remove"
            @click="askRemove(person)"
          >
            <span class="material-icons">close</span>
          </button>
        </div>
      </div>
      <button
        v-else
        type="button"
        class="prod-person-empty"
        @click="openAdd(role)"
      >
        <span class="material-icons">add</span>
        Add {{ memberRoleLabels[role].toLowerCase() }}
      </button>
    </section>

    <teleport to="body">
      <div
        v-if="showAdd"
        class="fixed inset-0 move-modal-overlay flex items-center justify-center z-50 p-4"
        @click.self="closeAdd"
      >
        <div class="move-card move-modal-card w-full max-w-md" role="dialog" aria-labelledby="add-person-title">
          <div class="move-modal-card__header">
            <div>
              <h3 id="add-person-title" class="move-modal-card__title">Add {{ memberRoleLabels[form.role] }}</h3>
              <p class="move-modal-card__subtitle">Pick a teammate or enter someone external.</p>
            </div>
            <button type="button" class="move-icon-btn" aria-label="Close" @click="closeAdd">
              <span class="material-icons">close</span>
            </button>
          </div>

          <form class="space-y-4" @submit.prevent="add">
            <div v-if="form.role !== 'presenter'" class="move-field">
              <label for="people-user">Workspace member</label>
              <MoveSelect
                id="people-user"
                v-model="form.user_id"
                block
                prefix-icon="group"
                placeholder="External / type a name"
                :options="teamOptions"
                @change="applyMember"
              />
            </div>
            <div v-else class="move-field">
              <label for="people-presenter">Accepted presenter</label>
              <MoveSelect
                id="people-presenter"
                v-model="form.presenter_application_id"
                block
                prefix-icon="mic"
                placeholder="Type a name instead"
                :options="presenterOptions"
                @change="applyPresenter"
              />
            </div>
            <div class="move-field">
              <label for="people-name">Name</label>
              <input id="people-name" v-model="form.name" :required="!form.user_id" class="move-input" placeholder="Full name" />
            </div>
            <div class="grid gap-4 sm:grid-cols-2">
              <div class="move-field">
                <label for="people-email">Email</label>
                <input id="people-email" v-model="form.email" type="email" class="move-input" placeholder="Optional" />
              </div>
              <div class="move-field">
                <label for="people-phone">Phone</label>
                <input id="people-phone" v-model="form.phone" class="move-input" placeholder="Optional" />
              </div>
            </div>
            <div v-if="form.role === 'crew'" class="move-field">
              <label for="people-crew">Crew title</label>
              <input id="people-crew" v-model="form.crew_title" class="move-input" placeholder="Camera, sound, lighting…" />
            </div>
            <p v-if="formError" class="move-alert-error">{{ formError }}</p>
            <div class="prod-form-actions">
              <button type="submit" class="move-btn-primary flex-1" :disabled="saving">
                <span class="material-icons text-lg">{{ saving ? 'sync' : 'check' }}</span>
                {{ saving ? 'Adding…' : 'Add' }}
              </button>
              <button type="button" class="move-btn-ghost" @click="closeAdd">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </teleport>

    <MoveConfirmDialog
      v-model="showRemoveModal"
      title="Remove person"
      subtitle="They will be unassigned from this production."
      :detail-title="personToRemove?.name || ''"
      :detail-body="personToRemove?.email || personToRemove?.phone || ''"
      detail-tone="danger"
      confirm-label="Remove"
      confirm-icon="person_remove"
      busy-label="Removing…"
      :busy="removing"
      :error="removeError"
      @confirm="confirmRemove"
      @cancel="closeRemove"
    />
  </div>
</template>

<script setup>
import { computed, inject, onMounted, reactive, ref } from 'vue'
import ProductionSectionHead from '../../components/production/ProductionSectionHead.vue'
import MoveConfirmDialog from '../../components/MoveConfirmDialog.vue'
import { productionApi } from '../../services/productionApi'
import { memberRoleLabels } from '../../config/productionNavigation'
import { initials } from '../../utils/productionDates'

const workspace = inject('productionWorkspace')
const production = workspace.production
const roles = ['talent', 'director', 'presenter', 'crew']
const roleIcons = {
  talent: 'star',
  director: 'movie',
  presenter: 'mic',
  crew: 'groups'
}
const team = ref([])
const presenters = ref([])
const showAdd = ref(false)
const saving = ref(false)
const formError = ref('')
const form = reactive({
  role: 'talent',
  user_id: '',
  presenter_application_id: '',
  name: '',
  email: '',
  phone: '',
  crew_title: ''
})

const teamOptions = computed(() => [
  { value: '', label: 'External / type a name' },
  ...team.value.map((user) => ({
    value: String(user.user_id),
    label: `${user.name} ${user.surname} (${user.email})`
  }))
])

const presenterOptions = computed(() => [
  { value: '', label: 'Type a name instead' },
  ...presenters.value.map((candidate) => ({
    value: String(candidate.id),
    label: candidate.name
  }))
])

const peopleFor = (role) => (production.value?.members || []).filter((person) => person.role === role)

function resetForm () {
  form.user_id = ''
  form.presenter_application_id = ''
  form.name = ''
  form.email = ''
  form.phone = ''
  form.crew_title = ''
  formError.value = ''
}

function openAdd (role) {
  resetForm()
  form.role = role
  showAdd.value = true
}

function closeAdd () {
  showAdd.value = false
  resetForm()
}

function applyMember () {
  const user = team.value.find((item) => String(item.user_id) === String(form.user_id))
  if (!user) return
  form.name = [user.name, user.surname].filter(Boolean).join(' ')
  form.email = user.email || ''
}

function applyPresenter () {
  const candidate = presenters.value.find((item) => String(item.id) === String(form.presenter_application_id))
  if (!candidate) return
  form.name = candidate.name
  form.email = candidate.email || ''
  form.phone = candidate.phone || ''
}

async function add () {
  formError.value = ''
  saving.value = true
  try {
    await productionApi.addMember(workspace.productionId.value, {
      role: form.role,
      user_id: form.user_id ? Number(form.user_id) : null,
      presenter_application_id: form.presenter_application_id ? Number(form.presenter_application_id) : null,
      name: form.name || undefined,
      email: form.email || null,
      phone: form.phone || null,
      crew_title: form.crew_title || null
    })
    closeAdd()
    await workspace.reload()
  } catch (e) {
    formError.value = e.response?.data?.message || Object.values(e.response?.data?.errors || {}).flat().join(' ') || 'Could not add person.'
  } finally {
    saving.value = false
  }
}

const showRemoveModal = ref(false)
const personToRemove = ref(null)
const removing = ref(false)
const removeError = ref('')

function askRemove (person) {
  personToRemove.value = person
  removeError.value = ''
  showRemoveModal.value = true
}

function closeRemove () {
  if (removing.value) return
  showRemoveModal.value = false
  personToRemove.value = null
  removeError.value = ''
}

async function confirmRemove () {
  const person = personToRemove.value
  if (!person || removing.value) return
  removing.value = true
  removeError.value = ''
  try {
    await productionApi.deleteMember(workspace.productionId.value, person.id)
    showRemoveModal.value = false
    personToRemove.value = null
    await workspace.reload()
  } catch (e) {
    removeError.value = e.response?.data?.message || 'Could not remove this person.'
  } finally {
    removing.value = false
  }
}

onMounted(async () => {
  const [teamBody, presenterBody] = await Promise.all([
    productionApi.assignableTeam(),
    productionApi.presenterCandidates()
  ])
  team.value = teamBody.data || []
  presenters.value = presenterBody.data || []
})
</script>
